import { call, put, select, takeEvery, takeLatest } from 'redux-saga/effects';
import { transactionActions } from '../transactionsSlice';
import * as transactionSelectors from '../transactionsSelectors';
import { desksActions } from 'store/desksSlice/desksSlice';
import { generalActions } from 'store/generalSlice/generalSlice';
import { selectCurrentDesk } from 'store/desksSlice/desksSelectors';
import { selectUser } from 'store/authSlice/authSelectors';
import { notificationHandler, flattenCamsConfig } from 'utils/utils';
import { sharedWorkerActions } from 'workers/sharedWorker/utils/utils';
import { cameraStreamManager } from 'services/CameraStream/CameraStreamManager';
import {
    pinLane as pinLaneRequest,
    unpinLane as unpinLaneRequest,
    getActiveStickyLanes,
    getStickyLaneErrorMessage,
} from 'services/stickyLaneService';

const UNPIN_REASON_MESSAGE = {
    INACTIVITY_TIMEOUT: 'Lane unpinned due to inactivity.',
    DESK_DISCONNECTED: 'Lane unpinned because your desk was disconnected.',
};

// /sticky-lane/active returns snake_case rows; map to the shape the videoWallPinnedLanes reducer expects.
const toVideoWallPinnedLanes = (lanes = []) =>
    lanes.map((lane) => ({ gateId: lane.gate_id, accountUsername: lane.account_username, pinnedAt: lane.timestamp }));

// While a lane is pinned its camera sources are marked AOT so streams stay alive across the
// submit/discard-and-start-new hand-off. gateId -> the deduped camConfig[] we pinned, so unpin cancels exactly that.
const aotCamerasByGate = new Map();

export const resetLaneAot = () => aotCamerasByGate.clear();

// camsConfig entries are already transformCameraConfiguration outputs (carry camId + videoData.streamType),
// usable by activateAot/cancelAot directly. Dedupe by camId across the gate's transactions.
function gateCameraConfigs(transactions, gateId) {
    const byCamId = new Map();
    transactions
        .filter((transaction) => transaction.gateId === gateId)
        .forEach((transaction) => {
            flattenCamsConfig(transaction.camsConfig).forEach((camera) => {
                if (camera?.camId != null && !byCamId.has(camera.camId)) byCamId.set(camera.camId, camera);
            });
        });
    return [...byCamId.values()];
}

export function* activateLaneAot(gateId) {
    const transactions = yield select(transactionSelectors.selectTransactions);
    const cameras = gateCameraConfigs(transactions, gateId);
    const subStreamCameraIds = yield select(transactionSelectors.selectSubStreamCameraIds);
    cameras.forEach((camera) => cameraStreamManager.activateAot(camera, { keepActiveStreamType: subStreamCameraIds.has(camera.camId) }));
    if (cameras.length) aotCamerasByGate.set(gateId, cameras);
}

function* cancelLaneAot(gateId) {
    let cameras = aotCamerasByGate.get(gateId);
    if (!cameras) {
        const transactions = yield select(transactionSelectors.selectTransactions);
        cameras = gateCameraConfigs(transactions, gateId);
    }
    cameras.forEach((camera) => cameraStreamManager.cancelAot(camera));
    aotCamerasByGate.delete(gateId);
}

// Pessimistic pin/unpin; the shared worker owns pinnedLanes and broadcasts to every tab on success.
function* toggleStickyLane({ gateId, pinned, request, successTitle, notify = true }) {
    if (gateId === undefined || gateId === null) return;

    const inProgress = yield select(transactionSelectors.selectIsLaneActionInProgress(gateId));
    if (inProgress) return;

    yield put(transactionActions.setLaneActionInProgress({ gateId, inProgress: true }));
    try {
        yield call(request, gateId);
        sharedWorkerActions.stickyLanePinToggle({ gateId, pinned });
        yield call(pinned ? activateLaneAot : cancelLaneAot, gateId);
        if (notify) yield call(notificationHandler, { title: successTitle, variant: 'success' });
    } catch (err) {
        yield call(notificationHandler, { title: getStickyLaneErrorMessage(err) });
    } finally {
        yield put(transactionActions.setLaneActionInProgress({ gateId, inProgress: false }));
    }
}

export function* pinLane({ payload: gateId }) {
    yield* toggleStickyLane({ gateId, pinned: true, request: pinLaneRequest, successTitle: 'Lane pinned' });
}

export function* unpinLane({ payload: gateId }) {
    yield* toggleStickyLane({ gateId, pinned: false, request: unpinLaneRequest, successTitle: 'Lane unpinned' });
}

// Bulk unpin every currently-pinned lane without a per-lane snackbar; used when switching to split view.
export function* unpinAllPinnedLanes() {
    const pinnedLanes = yield select(transactionSelectors.selectPinnedLanes);
    if (!pinnedLanes.length) return;
    for (const gateId of pinnedLanes) {
        yield* toggleStickyLane({ gateId, pinned: false, request: unpinLaneRequest, successTitle: 'Lane unpinned', notify: false });
    }
    yield call(notificationHandler, { title: 'Pinned lanes released for split view.', variant: 'info' });
}

// Hydration: fetch /sticky-lane/active once per desk session, after the desk is taken & confirmed.
let hydratedDeskId = null;
export const resetStickyLaneHydration = () => {
    hydratedDeskId = null;
};

export function* seedVideoWallPinnedLanes() {
    const activeStickyLanes = (yield call(getActiveStickyLanes)) || [];
    yield put(transactionActions.setVideoWallPinnedLanes(toVideoWallPinnedLanes(activeStickyLanes)));
}

// Reconciles local pin state with /sticky-lane/active: seeds the worker (this user's gates) and, when the
// video wall is on, repopulates its all-desks overlay. Shared by desk-confirm hydration and reconnect resync.
export function* syncPinnedLanesFromServer() {
    const activeStickyLanes = (yield call(getActiveStickyLanes)) || [];

    const { username } = yield select(selectUser);
    const gateIds = activeStickyLanes.filter((lane) => lane.account_username === username).map((lane) => lane.gate_id);
    sharedWorkerActions.stickyLanesSeed({ gateIds });

    const isVideoWallEnabled = yield select(transactionSelectors.selectIsVideoWallEnabled);
    if (isVideoWallEnabled) {
        yield put(transactionActions.setVideoWallPinnedLanes(toVideoWallPinnedLanes(activeStickyLanes)));
    }
}

function* hydrateStickyLanes({ payload: confirmed }) {
    const desk = yield select(selectCurrentDesk);
    if (!confirmed || !desk?.id) return;
    if (hydratedDeskId === desk.id) return;
    hydratedDeskId = desk.id;

    try {
        yield call(syncPinnedLanesFromServer);
    } catch (err) {
        hydratedDeskId = null; // allow a retry on the next confirmation
        yield call(notificationHandler, { err, title: 'Error fetching pinned lanes' });
    }
}

// STOMP down: the backend auto-unpins us server-side but we can't hear it, so reflect it locally (this user's
// gates only) without calling the unpin endpoint.
export function* clearPinnedLanesLocally() {
    const pinnedLanes = yield select(transactionSelectors.selectPinnedLanes);
    if (!pinnedLanes.length) return;

    for (const gateId of pinnedLanes) {
        yield call(cancelLaneAot, gateId);
        yield put(transactionActions.removeVideoWallPinnedLane(gateId));
    }
    sharedWorkerActions.stickyLanesSeed({ gateIds: [] });
}

// STOMP reconnected: re-check the backend and restore whatever is still pinned there (no pin request needed).
export function* resyncPinnedLanesOnReconnect() {
    try {
        yield call(syncPinnedLanesFromServer);
    } catch (err) {
        yield call(notificationHandler, { err, title: 'Error re-syncing pinned lanes' });
    }
}

// Desk-topic unpin: the backend closed our lane (inactivity / disconnect / manual echo).
export function* handleStickyLaneUnpinnedDeskMessage(data) {
    const gateId = data?.gate_id;
    if (gateId === undefined || gateId === null) return;
    sharedWorkerActions.stickyLanePinToggle({ gateId, pinned: false });
    yield call(cancelLaneAot, gateId);
    if (data.reason !== 'MANUAL') {
        yield call(notificationHandler, { title: UNPIN_REASON_MESSAGE[data.reason] ?? 'Lane unpinned.', variant: 'warning' });
    }
}

export function* onPinLane() {
    yield takeEvery(transactionActions.pinLaneStart.type, pinLane);
}

export function* onUnpinLane() {
    yield takeEvery(transactionActions.unpinLaneStart.type, unpinLane);
}

export function* onHydrateStickyLanes() {
    yield takeLatest(desksActions.setIsActiveDeskConfirmed.type, hydrateStickyLanes);
}

// A window can drop its SharedWorker port without the JS module resetting; clear the module-level guards so
// re-taking the same desk re-runs hydration instead of short-circuiting on a stale hydratedDeskId.
export function resetLaneStateOnWindowDisconnect({ payload: disconnected }) {
    if (!disconnected) return;
    resetStickyLaneHydration();
    resetLaneAot();
}

export function* onWindowDisconnectedFromSharedWorker() {
    yield takeEvery(generalActions.setIsWindowDisconnectedFromSharedWorker.type, resetLaneStateOnWindowDisconnect);
}
